<template>
  <v-row no-gutters>
    <v-col cols="5" class="border-right">
      <v-row no-gutters align="baseline" class="pt-2">
        <v-col cols="5" class="style-elem-text">
          {{ $i18n.t('dialog.style.symbolizationType') }}
        </v-col>
        <v-col cols="7" class="pr-2">
          <data-input
            class="style-select"
            v-model="symbolizationType"
            :items="symbolizationTypes"
            item-text="name"
            item-value="value"
            @change="changeSymbolizationType"
            :data-type="dataTypeSelect"
            :disabled="rangesLoading"
          >
          </data-input>
        </v-col>
      </v-row>
      <v-row
        no-gutters
        align="baseline"
        class="pt-2"
        v-if="['point', 'multipoint', 'polygon', 'multipolygon'].includes(geomType)"
      >
        <v-col cols="5" class="style-elem-text">
          {{ $i18n.t('dialog.style.symbolizationKind') }}
        </v-col>
        <v-col cols="7" class="pr-2">
          <data-input
            @change="changeSymbolizationKind"
            class="style-select"
            v-model="symbolizationKind"
            :items="symbolizationKinds"
            item-text="text"
            item-value="value"
            :data-type="dataTypeSelect"
            :disabled="rangesLoading"
          />
        </v-col>
      </v-row>
      <template v-if="symbolizationType === 'unique'">
        <v-row no-gutters align="center">
          <v-col cols="5" class="style-elem-text">
            {{ $i18n.t('dialog.style.attribute') }}
          </v-col>
          <v-col cols="7" class="pr-2">
            <data-input
              class="style-select"
              v-model="symbolizationAttribute"
              :items="attributes"
              item-text="text"
              item-value="value"
              :data-type="dataTypeSelect"
              @change="resetClassification"
              hide-details
            >
            </data-input>
          </v-col>
        </v-row>
        <v-row class="mb-5 pl-2 pt-6" no-gutters align="center">
          <v-col cols="3">
            <base-button
              :disabled="!symbolizationAttribute"
              @click="layer.style.uniques?.property ? openReclassify() : classify(false)"
              :translation-path="`dialog.style.${layer.style.uniques?.property ? 'reclassify' : 'classify'}`"
            ></base-button>
          </v-col>
          <template v-if="isValidClassification">
            <base-rounded-button
              icon="mdi-minus"
              icon-size="18"
              :custom-size="26"
              color="error"
              :tooltip="deletingActiveClassTooltip"
              :disabled="isDeletingActiveClassDisabled"
              @click="openDeleteClass"
            />
          </template>
        </v-row>
        <v-row class="border-top" no-gutters v-if="layer.style.uniques">
          <v-col cols="auto" v-if="isValidClassification">
            <style-dialog-content-classes
              :items="computedStyleUniquesValues"
              :active-item.sync="activeStyleClass"
              :layer-type="getLayerType(layer)"
              @setClasses="setStyleUniquesValues"
              @setActiveItem="setActiveClassByValue"
            />
          </v-col>
        </v-row>
      </template>
      <v-row class="border-top" no-gutters v-if="symbolizationType === 'single'">
        <v-col cols="12" class="mt-3 pr-2 pl-2">
          <span class="single-class">
            <v-row no-gutters align="center">
              <v-col cols="auto" class="pr-2">
                <layer-style-preview-icon is-icon :layer-type="getLayerType(layer)" :layer-style="layer.style" />
              </v-col>
              <v-col cols="auto">
                <span
                  v-if="
                    this.layer.style.labels &&
                      this.layer.style.labels.attributes &&
                      this.layer.style.labels.attributes.length > 0
                  "
                >
                  {{ computedLabels }}
                </span>
                <span v-else class="no-label">
                  {{ this.$i18n.t('dialog.style.noLabel') }}
                </span>
              </v-col>
            </v-row>
          </span>
        </v-col>
      </v-row>
      <template v-if="symbolizationType === 'ranges'">
        <v-form v-model="rangesParamsValid">
          <v-row no-gutters align="center">
            <v-col cols="5" class="style-elem-text pb-5">
              {{ $i18n.t('dialog.style.attribute') }}
            </v-col>
            <v-col cols="7" class="pr-2">
              <data-input
                class="style-select"
                v-model="rangesAttribute"
                :items="rangesAllowedAttributes"
                item-text="text"
                item-value="valueMvt"
                :data-type="dataTypeSelect"
                :rules="requiredRules"
                :disabled="rangesLoading"
                @change="resetRanges"
              />
            </v-col>
          </v-row>
          <v-row no-gutters align="center" class="pt-6">
            <v-col cols="5" class="style-elem-text pb-5">
              {{ $i18n.t('dialog.style.numberOfClasses') }}
            </v-col>
            <v-col cols="7" class="pr-2">
              <data-input
                class="style-select"
                v-model="numberOfClasses"
                :data-type="dataTypeInteger"
                :rules="[
                  v => !!v || v == 0 || this.$i18n.t('rules.required'),
                  v => v >= 2 || this.$i18n.t('rules.minValue', { value: 2 }),
                  v => v <= 20 || this.$i18n.t('rules.maxValue', { value: 20 }),
                ]"
                :disabled="rangesLoading"
                @change="resetRanges"
              />
            </v-col>
          </v-row>
        </v-form>
        <v-row class="mb-5 pl-2 pt-6" no-gutters>
          <v-col cols="3">
            <base-button
              :disabled="!rangesParamsValid"
              :loading="rangesLoading"
              :translation-path="`dialog.style.${layer.style.ranges?.property ? 'reclassify' : 'classify'}`"
              @click="layer.style.ranges?.property ? openReclassify() : getRanges(false)"
            />
          </v-col>
          <template v-if="isValidClassification">
            <base-rounded-button
              icon="mdi-minus"
              icon-size="18"
              :custom-size="26"
              color="error"
              :tooltip="deletingActiveRangeTooltip"
              :disabled="isDeletingActiveRangeDisabled"
              @click="openDeleteClass"
            />
            <base-rounded-button
              icon="mdi-plus"
              icon-size="18"
              :custom-size="26"
              color="success"
              :tooltip="$i18n.t('dialog.addNewRange')"
              @click="addNewRange"
            />
          </template>
        </v-row>
        <v-row class="border-top" no-gutters v-if="isValidClassification && computedStyleRangesValues">
          <v-col cols="12">
            <v-form v-model="rangesValid" ref="ranges-form">
              <v-data-table
                v-if="computedStyleRangesValues"
                :headers="rangesTableHeaders"
                :items="computedStyleRangesValues"
                disable-pagination
                hide-default-footer
              >
                <template #item.radio="{item}">
                  <v-radio-group v-model="activeRadioStyleClass" @change="setActiveClass(item)">
                    <v-radio :value="`${item['min-value']}-${item['max-value']}`"></v-radio>
                  </v-radio-group>
                </template>
                <template #item.symbol="{item}">
                  <layer-style-preview-icon is-icon :layer-type="getLayerType(layer)" :layer-style="item" />
                </template>
                <template #item.values="{item}">
                  <v-row no-gutters align="center" justify="center" class="text-body-2" v-if="item.ranges">
                    {{ $i18n.t('default.others') }}
                  </v-row>
                  <v-row v-else no-gutters align="center">
                    <v-col>
                      <data-input
                        class="style-select"
                        v-model="item['min-value']"
                        :data-type="rangesAttributeMetadata.dataType"
                        :rules="getMinValueRules(item['max-value'], layer.style.ranges.values.indexOf(item))"
                        @input="changePreviousValue(item, $event)"
                        hide-details
                        timestamp
                      />
                    </v-col>
                    <v-col cols="auto" class="px-2">-</v-col>
                    <v-col>
                      <data-input
                        class="style-select"
                        v-model="item['max-value']"
                        :data-type="rangesAttributeMetadata.dataType"
                        :rules="getMaxValueRules(item['min-value'], layer.style.ranges.values.indexOf(item))"
                        @input="changeNextValue(item, $event)"
                        hide-details
                        timestamp
                      />
                    </v-col>
                  </v-row>
                </template>
                <template #item.label="{item}">
                  <v-row no-gutters align="center" justify="center" class="text-body-2" v-if="item.ranges">
                    {{ $i18n.t('default.others') }}
                  </v-row>
                  <data-input
                    v-else
                    class="style-select"
                    v-model="item.label"
                    :data-type="dataTypeText"
                    :rules="[v => !!v || v == 0 || this.$i18n.t('rules.required')]"
                    hide-details
                  />
                </template>
              </v-data-table>
            </v-form>
          </v-col>
        </v-row>
      </template>
    </v-col>
    <v-col cols="7">
      <style-dialog-style-tabs
        v-if="isSingleStyleVisible"
        :is-valid.sync="isValidStyle"
        :attributes="attributes"
        :geometry-type="geomType"
        :layerStyle.sync="currentStyle"
        :layerId="layer.id"
        :layerName="layer.name"
        :layerScope="layer.layer_scope"
        :is-admin="isAdmin"
        :symbolization-kind="symbolizationKind"
        :symbolization-type="symbolizationType"
        :save-default-style-function="saveDefaultStyle"
        @export="exportStyle"
        @import="importStyle"
      />
    </v-col>
    <main-dialog
      v-if="isSetClassesDialogLoaded"
      :is-visible.sync="isSetClassesDialogVisible"
      :title="$i18n.t('dialog.style.applyStyle')"
      :body="setClassesDialogBody"
      agree-text="dialog.agreeApply"
      @agree="setClassesDialogAgree"
    />
    <main-dialog
      v-if="reclassifyDialogLoaded"
      :is-visible.sync="reclassifyDialogVisible"
      :loading="rangesLoading"
      :title="$i18n.t('dialog.style.reclassify')"
      :body="$i18n.t('dialog.style.reclassifyConfirm')"
      agree-text="dialog.agreeReclassify"
      @agree="reclassifyAgree"
    />
    <main-dialog
      v-if="deleteClassLoaded"
      :is-visible.sync="deleteClassVisible"
      :title="$i18n.t('dialog.style.deleteActiveClass')"
      :body="$i18n.t('dialog.style.deleteActiveClassConfirm')"
      agree-text="dialog.agreeDelete"
      @agree="deleteClassAgree"
    />
  </v-row>
</template>

<script>
import { call, get } from 'vuex-pathify';
import { scaleLinear } from 'd3-scale';

import DataInput from '@/components/DataInput';
import LayerStylePreviewIcon from '@/components/LayerStylePreviewIcon';
import StyleDialogStyleTabs from '@/components/StyleDialogStyleTabs';
import StyleDialogContentClasses from '@/components/StyleDialogContentClasses';

import stylePreviewUtilities from '@/mixins/stylePreviewUtilities';

export default {
  name: 'StyleDialogContent',
  mixins: [stylePreviewUtilities],
  components: {
    DataInput,
    LayerStylePreviewIcon,
    StyleDialogStyleTabs,
    MainDialog: () => import('@/components/MainDialog'),
    StyleDialogContentClasses,
  },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    layer: {
      type: Object,
      required: true,
    },
    attributes: {
      type: Array,
      required: true,
    },
    geomType: {
      type: String,
      required: true,
    },
    localeTimeFormat: {
      validator: value => {
        return ['LT', 'LTS'].indexOf(value) !== -1;
      },
      default: 'LT',
    },
    localeDateFormat: {
      validatior: value => {
        return ['ll', 'l', 'LL', 'L'].indexOf(value) !== -1;
      },
      default: 'L',
    },
    dateTimeFormat: {
      type: String,
      default: 'date, time',
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
  },
  data: vm => ({
    currentSetClassesValues: undefined,
    isSetClassesDialogLoaded: false,
    isSetClassesDialogVisible: false,
    activeRadioStyleClass: null,
    styleTab: 0,
    styleTabs: [{ name: vm.$i18n.t('dialog.style.style') }, { name: vm.$i18n.t('dialog.style.labels') }],
    symbolizationType: 'single',
    symbolizationTypes: [
      { name: vm.$i18n.t('dialog.style.single'), value: 'single' },
      { name: vm.$i18n.t('dialog.style.unique'), value: 'unique' },
      { name: vm.$i18n.t('dialog.style.gradual'), value: 'ranges' },
    ],
    symbolizationAttribute: '',
    numberOfClasses: null,
    rangesAttribute: '',
    rangesLoading: false,
    rangesParamsValid: false,
    rangesValid: false,
    defaultStyleProps: {
      'fill-opacity': 1,
      minzoom: 10,
      maxzoom: null,
      labels: { attributes: [] },
    },
    activeStyleClass: {},
    isValidClassification: false,
    isValidStyle: false,
    unchangedStyle: {},
    dataTypeSelect: {
      name: 'select',
    },
    dataTypeInteger: {
      name: 'integer',
    },
    dataTypeText: {
      name: 'text',
    },
    symbolizationKind: '',
    reclassifyDialogLoaded: false,
    reclassifyDialogVisible: false,
    deleteClassLoaded: false,
    deleteClassVisible: false,
  }),
  computed: {
    icons: get('layers/icons'),
    maxUniqueStyleClasses() {
      return parseInt(process.env.VUE_APP_MAX_UNIQUE_STYLE_CLASSES || 100) || 100;
    },
    computedLabels() {
      const selectedAttributes = this.layer.style.labels ? this.layer.style.labels.attributes || [] : [];
      return this.attributes
        .filter(a => selectedAttributes.includes(a.valueLabel))
        .map(a => a.text)
        .join(', ');
    },
    computedIsValid() {
      return (
        this.isValidClassification &&
        this.isValidStyle &&
        ((this.rangesValid && this.rangesParamsValid) || !this.layer.style.ranges) &&
        (this.symbolizationType !== 'ranges' || this.layer.style.ranges.values.length >= 2)
      );
    },
    computedStyleRangesValues() {
      const defaultStyle = this.layer.style;
      return this.layer.style.ranges?.values.concat([defaultStyle]);
    },
    computedStyleUniquesValues() {
      const defaultStyle = this.layer.style;
      return this.$_objectToArray(this.layer.style.uniques?.values).concat([defaultStyle]);
    },
    currentStyle: {
      get() {
        return this.symbolizationType === 'single' ? this.layer.style : this.activeStyleClass;
      },
      set(nV) {
        if (this.symbolizationType === 'single') {
          this.layer.style = nV;
        } else {
          this.activeStyleClass = nV;
        }
      },
    },
    localeDateTimeFormat() {
      return this.dateTimeFormat.replace('time', this.localeTimeFormat).replace('date', this.localeDateFormat);
    },
    localeDateTimeFormatsDict() {
      return {
        datetime: 'localeDateTimeFormat',
        date: 'localeDateFormat',
        time: 'localeTimeFormat',
      };
    },
    rangesAllowedAttributes() {
      return this.attributes.filter(
        attribute =>
          !attribute.isRelational &&
          ['integer', 'float', 'decimal', 'datetime', 'date', 'time'].includes(attribute.dataType.name)
      );
    },
    deletingActiveRangeTooltip() {
      if (!this.activeStyleClass) {
        return this.$i18n.t('dialog.selectClassToDeleteIt');
      }
      return this.isDeletingActiveRangeDisabled
        ? this.$i18n.t('dialog.cantDeleteClass', { class: this.$i18n.t('default.others') })
        : this.$i18n.t('dialog.deleteActiveRange');
    },
    deletingActiveClassTooltip() {
      if (!this.activeStyleClass) {
        return this.$i18n.t('dialog.selectClassToDeleteIt');
      }
      return this.isDeletingActiveClassDisabled
        ? this.$i18n.t('dialog.cantDeleteClass', { class: this.$i18n.t('default.others') })
        : this.$i18n.t('dialog.deleteActiveClass');
    },
    isDeletingActiveRangeDisabled() {
      if (!this.activeStyleClass) {
        return true;
      }
      return Boolean(
        this.activeStyleClass &&
          !this.activeStyleClass['min-value'] &&
          !this.activeStyleClass['max-value'] &&
          this.activeStyleClass.ranges
      );
    },
    isDeletingActiveClassDisabled() {
      if (!this.activeStyleClass) {
        return true;
      }
      return Boolean(this.activeStyleClass && this.activeStyleClass['uniques']);
    },
    isSingleStyleVisible() {
      return this.symbolizationType === 'single' || this.activeStyleClass;
    },
    rangesAttributeMetadata() {
      const attribute =
        this.rangesAttribute.substring(0, 8) === 'mvt_int_' ? this.rangesAttribute.substring(8) : this.rangesAttribute;
      return this.attributes.find(attr => attr.value === attribute);
    },
    rangesTableHeaders() {
      return [
        { value: 'radio', text: '', align: 'center', sortable: false },
        { value: 'symbol', text: this.$i18n.t('dialog.style.symbol'), align: 'center', sortable: false },
        { value: 'values', text: this.$i18n.t('dialog.style.values'), align: 'center', sortable: false },
        { value: 'label', text: this.$i18n.t('dialog.style.legend'), align: 'center', sortable: false },
      ];
    },
    requiredRules() {
      return [v => !!v || this.$i18n.t('rules.required')];
    },
    symbolizationKinds() {
      return this.geomType.includes('point')
        ? [
            {
              value: 'point',
              text: this.$i18n.t('dialog.style.point'),
            },
            {
              value: 'icon',
              text: this.$i18n.t('dialog.style.icon'),
            },
          ]
        : [
            {
              value: 'polygon',
              text: this.$i18n.t('dialog.style.colorFill'),
            },
            {
              value: 'icon',
              text: this.$i18n.t('dialog.style.iconFill'),
            },
          ];
    },
    setClassesDialogBody() {
      const { stylePropsList, propertyTranslation, styleType } = this.currentSetClassesValues;
      const all = stylePropsList ? true : false;
      return all
        ? this.$i18n.t(`dialog.style.copy${this.$_capitalize(styleType)}AllPropertyBody`)
        : this.$i18n.t('dialog.style.copyStylePropertyBody', {
            name: propertyTranslation,
          });
    },
  },
  watch: {
    computedIsValid(nV) {
      this.$emit('update:isValid', nV);
    },
  },
  methods: {
    setStyleUniquesValues(values) {
      const indexOfDefaultStyle = values.findIndex(element => !Object.keys(element).includes('value'));
      const defaultStyle = values[indexOfDefaultStyle];
      values.splice(indexOfDefaultStyle, 1);
      const uniqueValues = this.$_arrayToObject(values, 'value');
      this.layer.style.uniques.values = uniqueValues;
      this.layer.style = { ...this.layer.style, ...defaultStyle };
      this.refreshActiveClass();
    },
    getDataSourceAttributeStatistics: call('layers/getDataSourceAttributeStatistics'),
    getLayerUniqueStyle: call('layers/getLayerUniqueStyle'),
    addNewRange(e, color = '#000000') {
      let range = {};
      if (this.geomType === 'point' || this.geomType === 'multipoint') {
        range = {
          ...(this.symbolizationKind === 'point'
            ? {
                'circle-color': color,
                'circle-radius': 3,
              }
            : { externalGraphic: this.icons[0].name /*, 'icon-size': 1*/ }),
        };
      } else if (this.geomType === 'linestring' || this.geomType === 'multilinestring') {
        range = {
          'line-dash': [],
          'line-color': color,
          'line-width': 1,
        };
      } else if (this.geomType === 'polygon' || this.geomType === 'multipolygon') {
        range = {
          'line-dash': [],
          ...(this.symbolizationKind === 'polygon'
            ? {
                'fill-color': color,
              }
            : { externalGraphic: this.icons[0].name /*, 'icon-size': 1*/ }),
          'line-width': 1,
          'fill-outline-color': '#000000',
          'fill-outline-opacity': 1,
        };
      }
      range = {
        ...range,
        ...this.defaultStyleProps,
        'min-value': null,
        'max-value': null,
        label: '',
      };
      this.layer.style.ranges.values.push(range);
    },
    changeSymbolizationKind(value) {
      if (value === 'point') {
        delete this.layer.style.externalGraphic;
        delete this.layer.style['icon-size'];
        this.$set(this.layer.style, 'circle-color', '#000000');
        this.$set(this.layer.style, 'circle-radius', 3);
      } else if (value === 'polygon') {
        delete this.layer.style.externalGraphic;
        // delete this.layer.style['icon-size'];
        this.$set(this.layer.style, 'fill-color', '#000000');
      } else {
        delete this.layer.style['circle-color'];
        delete this.layer.style['circle-radius'];
        delete this.layer.style['fill-color'];
        this.$set(this.layer.style, 'externalGraphic', this.icons[0].name);
        if (this.geomType.includes('point')) {
          this.$set(this.layer.style, 'icon-size', 1);
        }
      }
      if (this.layer.style.uniques) {
        for (const unique in this.layer.style.uniques.values) {
          const uniqueStyle = this.layer.style.uniques.values[unique];
          if (value === 'point') {
            delete uniqueStyle.externalGraphic;
            delete uniqueStyle['icon-size'];
            this.$set(uniqueStyle, 'circle-color', '#000000');
            this.$set(uniqueStyle, 'circle-radius', 3);
          } else if (value === 'polygon') {
            delete uniqueStyle.externalGraphic;
            // delete uniqueStyle['icon-size'];
            this.$set(uniqueStyle, 'fill-color', '#000000');
          } else {
            delete uniqueStyle['circle-color'];
            delete uniqueStyle['circle-radius'];
            delete uniqueStyle['fill-color'];
            this.$set(uniqueStyle, 'externalGraphic', this.icons[0].name);
            if (this.geomType.includes('point')) {
              this.$set(this.layer.style, 'icon-size', 1);
            }
          }
        }
      } else if (this.layer.style.ranges) {
        for (const rangeStyle of this.layer.style.ranges.values) {
          if (value === 'point') {
            delete rangeStyle.externalGraphic;
            delete rangeStyle['icon-size'];
            this.$set(rangeStyle, 'circle-color', '#000000');
            this.$set(rangeStyle, 'circle-radius', 3);
          } else if (value === 'polygon') {
            delete rangeStyle.externalGraphic;
            // delete rangeStyle['icon-size'];
            this.$set(rangeStyle, 'fill-color', '#000000');
          } else {
            delete rangeStyle['circle-color'];
            delete rangeStyle['circle-radius'];
            delete rangeStyle['fill-color'];
            this.$set(rangeStyle, 'externalGraphic', this.icons[0].name);
            if (this.geomType.includes('point')) {
              this.$set(this.layer.style, 'icon-size', 1);
            }
          }
        }
      }
    },
    changeNextValue(item, value) {
      const index = this.layer.style.ranges.values.indexOf(item);
      const nextItem = this.layer.style.ranges.values[index + 1];
      if (nextItem && value < nextItem['max-value']) {
        this.layer.style.ranges.values[index + 1]['min-value'] = value;
      }
      setTimeout(() => {
        this.$refs?.['ranges-form'].validate();
      });
    },
    changePreviousValue(item, value) {
      const index = this.layer.style.ranges.values.indexOf(item);
      const previousItem = this.layer.style.ranges.values[index - 1];
      if (previousItem && value > previousItem['min-value']) {
        this.layer.style.ranges.values[index - 1]['max-value'] = value;
      }
      setTimeout(() => {
        this.$refs?.['ranges-form'].validate();
      });
    },
    deleteActiveClass() {
      this.$delete(this.layer.style.uniques.values, this.activeStyleClass.value);
      this.setActiveClass(this.computedStyleUniquesValues[0]);
    },
    deleteActiveRange() {
      const filteredRanges = JSON.parse(
        JSON.stringify(this.computedStyleRangesValues.filter(value => value !== this.activeStyleClass))
      );
      this.layer.style.ranges.values = filteredRanges.filter(range => !range.ranges);
      const firstValue = filteredRanges[0];
      this.setActiveClass(firstValue);
      this.activeRadioStyleClass = `${firstValue['min-value']}-${firstValue['max-value']}`;
    },
    exportStyle() {
      const content = JSON.stringify(this.layer.style);
      let file = new Blob([content], { type: 'text/plain' });
      this.$_saveFile(URL.createObjectURL(file), `${this.layer.name}.style`);
    },
    getMaxValueRules(minValue, index) {
      const format = this[this.localeDateTimeFormatsDict[this.rangesAttributeMetadata.dataType.name]];
      const rules = [
        v => !!v || v == 0 || '',
        v => {
          if (format) {
            const computedV = this.$dayjs(v, format).unix();
            return computedV > minValue;
          }
          return v > minValue;
        },
      ];
      const nextItem = this.layer.style.ranges.values[index + 1];
      if (nextItem) {
        rules.push(v => {
          if (format) {
            const computedV = this.$dayjs(v, format).unix();
            return computedV < nextItem['max-value'];
          }
          return v < nextItem['max-value'];
        });
      }
      return rules;
    },
    getMinValueRules(maxValue, index) {
      const format = this[this.localeDateTimeFormatsDict[this.rangesAttributeMetadata.dataType.name]];
      const rules = [
        v => !!v || v == 0 || '',
        v => {
          if (format) {
            const computedV = this.$dayjs(v, format).unix();
            return computedV < maxValue;
          }
          return v < maxValue;
        },
      ];
      const previousItem = this.layer.style.ranges.values[index - 1];
      if (previousItem) {
        rules.push(v => {
          if (format) {
            const computedV = this.$dayjs(v, format).unix();
            return computedV > previousItem['min-value'];
          }
          return v > previousItem['min-value'];
        });
      }
      return rules;
    },
    importStyle(style) {
      this.$set(this.layer, 'style', style);
      if (this.layer.style.uniques) {
        this.symbolizationType = 'unique';
      } else if (this.layer.style.ranges) {
        this.symbolizationType = 'ranges';
      } else {
        this.symbolizationType = 'single';
      }
      if (style['circle-color']) {
        this.symbolizationKind = 'point';
      } else if (style['fill-color']) {
        this.symbolizationKind = 'polygon';
      } else if (style['externalGraphic']) {
        this.symbolizationKind = 'icon';
      }
      this.symbolizationAttribute = this.layer.style.uniques ? this.layer.style.uniques.property : '';
      this.rangesAttribute = this.layer.style.ranges ? this.layer.style.ranges.property : '';
      this.numberOfClasses = this.layer.style.ranges ? this.layer.style.ranges.values.length : null;
      if (this.symbolizationType === 'unique') {
        this.setActiveClass(this.computedStyleUniquesValues[0]);
      } else if (this.symbolizationType === 'ranges') {
        const firstValue = this.computedStyleRangesValues[0];
        this.setActiveClass(firstValue);
        this.activeRadioStyleClass = `${firstValue['min-value']}-${firstValue['max-value']}`;
      }
      this.isValidClassification = true;
    },
    async getRanges(re) {
      this.rangesLoading = true;
      try {
        const stats = await this.getDataSourceAttributeStatistics({
          attributeName:
            this.rangesAttribute.substring(0, 8) === 'mvt_int_'
              ? this.rangesAttribute.substring(8)
              : this.rangesAttribute,
          dataSourceName: this.layer.data_source_name,
        });
        const { min = null, max = null } = stats;
        if (min === max || (!min && min !== 0) || (!max && max !== 0)) {
          this.$store.set('snackbar/PUSH_MESSAGE!', {
            message: this.$i18n.t('dialog.tooFewValues'),
          });
          this.rangesLoading = false;
          return;
        }
        const values = scaleLinear()
          .domain([1, this.numberOfClasses + 1])
          .range([min, max]);
        const rampColors = Array.from({ length: this.numberOfClasses }, () => this.$_getRandomColor());
        const rangesValues = [];
        const roundNumber = ['datetime', 'date', 'time'].includes(this.rangesAttributeMetadata.dataType.name) ? 1 : 100;
        for (let idx = 2; idx < this.numberOfClasses + 1; idx++) {
          const roundValue = Math.round(values(idx) * roundNumber) / roundNumber;
          rangesValues.push(roundValue);
        }

        const oldClasses = this.layer.style.ranges?.values;
        const getRange = (
          a,
          b,
          format = this[this.localeDateTimeFormatsDict[this.rangesAttributeMetadata.dataType.name]]
        ) => {
          return {
            'min-value': a,
            'max-value': b,
            label: `${format ? this.$dayjs.unix(a).format(format) : a} - ${
              format ? this.$dayjs.unix(b).format(format) : b
            }`,
          };
        };
        const ranges = rampColors.map((color, idx) => {
          let range = {};
          if (re && this.numberOfClasses === oldClasses.length) {
            range = oldClasses[idx];
          } else if (this.geomType === 'point' || this.geomType === 'multipoint') {
            range = {
              ...JSON.parse(JSON.stringify(this.defaultStyleProps)),
              ...(this.symbolizationKind === 'point'
                ? {
                    'circle-color': color,
                    'circle-radius': 3,
                  }
                : { externalGraphic: this.icons[0].name, 'icon-size': 1 }),
            };
          } else if (this.geomType === 'linestring' || this.geomType === 'multilinestring') {
            range = {
              ...JSON.parse(JSON.stringify(this.defaultStyleProps)),
              'line-dash': [],
              'line-color': color,
              'line-width': 1,
            };
          } else if (this.geomType === 'polygon' || this.geomType === 'multipolygon') {
            range = {
              ...JSON.parse(JSON.stringify(this.defaultStyleProps)),
              'line-dash': [],
              ...(this.symbolizationKind === 'polygon'
                ? {
                    'fill-color': color,
                  }
                : { externalGraphic: this.icons[0].name /*, 'icon-size': 1*/ }),
              'line-width': 1,
              'fill-outline-color': '#000000',
              'fill-outline-opacity': 1,
            };
          }
          if (idx === 0) {
            range = { ...range, ...getRange(min, rangesValues[idx]) };
          } else if (idx === this.numberOfClasses - 1) {
            range = { ...range, ...getRange(rangesValues[idx - 1], max) };
          } else {
            range = { ...range, ...getRange(rangesValues[idx - 1], rangesValues[idx]) };
          }
          if (re && this.numberOfClasses === oldClasses.length && oldClasses[idx]) {
            const oldClassDefaultLabel = getRange(oldClasses[idx]['min-value'], oldClasses[idx]['max-value'])?.label;
            if (oldClassDefaultLabel !== oldClasses[idx].label) range.label = oldClasses[idx].label;
          }
          return range;
        });
        const style = JSON.parse(JSON.stringify(this.layer.style));
        style.ranges = { values: ranges, property: this.rangesAttribute };
        this.$set(this.layer, 'style', style);
        this.activeRadioStyleClass = `${ranges[0]['min-value']}-${ranges[0]['max-value']}`;
        this.setActiveClass(ranges[0]);
        this.isValidClassification = true;
        this.rangesLoading = false;
      } catch {
        this.rangesLoading = false;
      }
    },
    refreshActiveClass() {
      this.setActiveClassByValue(this.activeStyleClass.value);
    },
    setActiveClass(styleClass) {
      this.activeStyleClass = styleClass;
    },
    setActiveClassByValue(value) {
      this.setActiveClass(value ? this.layer.style.uniques.values[value] : this.layer.style);
    },
    changeSymbolizationType(value) {
      if (this.unchangedStyle.uniques && value === 'unique') {
        this.layer.style = this.unchangedStyle;
        if (this.layer.style['circle-color']) {
          this.symbolizationKind = 'point';
        } else if (this.layer.style['fill-color']) {
          this.symbolizationKind = 'polygon';
        } else if (this.layer.style['externalGraphic']) {
          this.symbolizationKind = 'icon';
        }
        this.symbolizationAttribute = this.layer.style.uniques.property;
        this.isValidClassification = true;
        return;
      } else if (this.unchangedStyle.ranges && value === 'ranges') {
        this.layer.style = this.unchangedStyle;
        if (this.layer.style['circle-color']) {
          this.symbolizationKind = 'point';
        } else if (this.layer.style['fill-color']) {
          this.symbolizationKind = 'polygon';
        } else if (this.layer.style['externalGraphic']) {
          this.symbolizationKind = 'icon';
        }
        this.rangesAttribute = this.layer.style.ranges.property;
        this.isValidClassification = true;
        return;
      } else if (!this.unchangedStyle.ranges && !this.unchangedStyle.uniques && value === 'single') {
        this.layer.style = this.unchangedStyle;
        if (this.layer.style['circle-color']) {
          this.symbolizationKind = 'point';
        } else if (this.layer.style['fill-color']) {
          this.symbolizationKind = 'polygon';
        } else if (this.layer.style['externalGraphic']) {
          this.symbolizationKind = 'icon';
        }
        this.isValidClassification = true;
        return;
      }
      this.symbolizationAttribute = '';
      this.rangesAttribute = '';
      this.numberOfClasses = null;
      let style = {};
      if (this.geomType === 'point' || this.geomType === 'multipoint') {
        style = {
          'circle-color': '#c7c7c7',
          'circle-radius': 3,
        };
      } else if (this.geomType === 'linestring' || this.geomType === 'multilinestring') {
        style = {
          'line-dash': [],
          'line-color': '#c7c7c7',
          'line-width': 1,
        };
      } else if (this.geomType === 'polygon' || this.geomType === 'multipolygon') {
        style = {
          'line-dash': [],
          'fill-color': '#c7c7c7',
          'line-width': 1,
          'fill-outline-color': '#000000',
          'fill-outline-opacity': 1,
        };
      }
      this.layer.style = {
        ...style,
        ...this.defaultStyleProps,
      };
      if (value === 'unique') {
        this.isValidClassification = false;
        this.layer.style.uniques = { values: {}, property: '' };
        this.activeStyleClass = undefined;
      } else if (value === 'ranges') {
        this.isValidClassification = false;
        this.layer.style.ranges = { values: [], property: '' };
        this.activeStyleClass = undefined;
      } else {
        this.isValidClassification = true;
      }
      if (this.layer.style['circle-color']) {
        this.symbolizationKind = 'point';
      } else if (this.layer.style['fill-color']) {
        this.symbolizationKind = 'polygon';
      } else if (this.layer.style['externalGraphic']) {
        this.symbolizationKind = 'icon';
      }
    },
    async classify(re = false) {
      const r = await this.getLayerUniqueStyle({
        layer_id: this.layer.layer_id,
        attribute_name: this.symbolizationAttribute,
        signature_style:
          ['point', 'multipoint', 'polygon', 'multipolygon'].includes(this.geomType) &&
          this.symbolizationKind === 'icon',
      });
      if (r.data.data.classes.length > this.maxUniqueStyleClasses) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.toManyClasses'),
        });
        this.isValidClassification = false;
        return;
      }
      const oldClasses = this.layer.style.uniques?.values;
      const values = r.data.data.classes.reduce((total, current, index) => {
        const value = current.value;
        if (value || value === '' || value === false) {
          if (re && oldClasses?.[value]) {
            total[value] = oldClasses[value];
          } else {
            total[value] = {
              value,
              label: current.label,
              ...current.style,
              order:
                (this.layer.style.order || this.layer.style.order === 0) && this.layer.style.order <= index
                  ? index + 1
                  : index,
            };
          }
        }
        return total;
      }, {});
      const uniqueStyle = {
        ...(re ? { ...this.layer.style } : { ...r.data.data.default, order: Object.keys(values).length }),
        ...{ uniques: { values, property: this.symbolizationAttribute } },
      };
      this.setActiveClass(Object.values(uniqueStyle.uniques.values)[0]);
      this.layer.style = uniqueStyle;
      this.$forceUpdate();
      this.isValidClassification = true;
    },
    resetClassification() {
      this.isValidClassification = false;
      this.layer.style.uniques = { values: [], property: '' };
      this.activeStyleClass = undefined;
    },
    resetRanges() {
      this.isValidClassification = false;
      this.layer.style.ranges = { values: [], property: '' };
      this.activeStyleClass = undefined;
    },
    setClassesValues(v) {
      this.currentSetClassesValues = v;
      this.isSetClassesDialogLoaded = true;
      this.isSetClassesDialogVisible = true;
    },
    setClassesDialogAgree() {
      const all = this.currentSetClassesValues.stylePropsList ? true : false;
      this[all ? 'setAllClassesAllPropertiesValues' : 'setAllClassesPropertyValue']();
    },
    setAllClassesAllPropertiesValues() {
      const { stylePropsList, styleType } = this.currentSetClassesValues;
      const activeStyleClass = JSON.parse(
        JSON.stringify(styleType === 'style' ? this.activeStyleClass : this.activeStyleClass.labels)
      );
      const stylePropsToDeleteList = stylePropsList.filter(
        styleProp => !Object.keys(activeStyleClass).includes(styleProp)
      );
      for (const key of Object.keys(activeStyleClass)) {
        if (!stylePropsList.includes(key)) {
          delete activeStyleClass[key];
        }
      }
      if (styleType === 'style') {
        this.$set(this.layer, 'style', {
          ...this.$_filterObjectKeys(this.layer.style, { notAllowedKeys: stylePropsToDeleteList }),
          ...activeStyleClass,
        });
      } else if (styleType === 'label') {
        this.$set(this.layer.style, 'labels', {
          ...this.$_filterObjectKeys(this.layer.style.labels, { notAllowedKeys: stylePropsToDeleteList }),
          ...activeStyleClass,
        });
      }
      if (this.layer.style.uniques) {
        for (const uniqueValue of Object.keys(this.layer.style.uniques.values)) {
          if (styleType === 'style') {
            const style = this.layer.style.uniques.values;
            this.$set(style, uniqueValue, {
              ...this.$_filterObjectKeys(style[uniqueValue], { notAllowedKeys: stylePropsToDeleteList }),
              ...activeStyleClass,
            });
          } else if (styleType === 'label') {
            const style = this.layer.style.uniques.values[uniqueValue];
            this.$set(style, 'labels', {
              ...this.$_filterObjectKeys(style.labels, { notAllowedKeys: stylePropsToDeleteList }),
              ...activeStyleClass,
            });
          }
        }
      } else if (this.layer.style.ranges) {
        for (const uniqueRangesIdx in this.layer.style.ranges.values) {
          if (styleType === 'style') {
            this.$set(this.layer.style.ranges.values, uniqueRangesIdx, {
              ...this.$_filterObjectKeys(this.layer.style.ranges.values[uniqueRangesIdx], {
                notAllowedKeys: stylePropsToDeleteList,
              }),
              ...activeStyleClass,
            });
          } else if (styleType === 'label') {
            const style = this.layer.style.ranges.values[uniqueRangesIdx];
            this.$set(style, 'labels', {
              ...this.$_filterObjectKeys(style.labels, { notAllowedKeys: stylePropsToDeleteList }),
              ...activeStyleClass,
            });
          }
        }
      }
      this.isSetClassesDialogVisible = false;
    },
    setAllClassesPropertyValue() {
      const {
        property,
        value,
        subValues,
        subPropertiesToDelete,
        styleType,
        copyMainParamWithSubsProps,
      } = this.currentSetClassesValues;
      if (styleType === 'style') {
        if (subValues) {
          Object.keys(subValues).forEach(subValueProperty => {
            this.$set(this.layer.style, subValueProperty, subValues[subValueProperty]);
            subPropertiesToDelete.forEach(subProp => {
              this.$delete(this.layer.style, subProp);
            });
          });
        }
        if (!subValues || copyMainParamWithSubsProps) {
          this.$set(this.layer.style, property, value);
        }
      } else if (styleType === 'label') {
        if (subValues) {
          Object.keys(subValues).forEach(subValueProperty => {
            this.$set(this.layer.style.labels, subValueProperty, subValues[subValueProperty]);
          });
          subPropertiesToDelete.forEach(subProp => {
            this.$delete(this.layer.style.labels, subProp);
          });
        }
        if (!subValues || copyMainParamWithSubsProps) {
          this.$set(this.layer.style.labels, property, value);
        }
      }
      if (this.layer.style.uniques) {
        for (const uniqueValue of Object.keys(this.layer.style.uniques.values)) {
          const style = this.layer.style.uniques.values[uniqueValue];
          if (styleType === 'style') {
            if (subValues) {
              Object.keys(subValues).forEach(subValueProperty => {
                this.$set(style, subValueProperty, subValues[subValueProperty]);
              });
              subPropertiesToDelete.forEach(subProp => {
                this.$delete(style, subProp);
              });
            }
            if (!subValues || copyMainParamWithSubsProps) {
              this.$set(style, property, value);
            }
          } else if (styleType === 'label') {
            if (subValues) {
              Object.keys(subValues).forEach(subValueProperty => {
                this.$set(style.labels, subValueProperty, subValues[subValueProperty]);
              });
              subPropertiesToDelete.forEach(subProp => {
                this.$delete(style.labels, subProp);
              });
            }
            if (!subValues || copyMainParamWithSubsProps) {
              this.$set(style.labels, property, value);
            }
          }
        }
      } else if (this.layer.style.ranges) {
        for (const uniqueRanges of this.layer.style.ranges.values) {
          if (styleType === 'style') {
            if (subValues) {
              Object.keys(subValues).forEach(subValueProperty => {
                this.$set(uniqueRanges, subValueProperty, subValues[subValueProperty]);
              });
              subPropertiesToDelete.forEach(subProp => {
                this.$delete(uniqueRanges, subProp);
              });
            }
            if (!subValues || copyMainParamWithSubsProps) {
              this.$set(uniqueRanges, property, value);
            }
          } else if (styleType === 'label') {
            if (subValues) {
              Object.keys(subValues).forEach(subValueProperty => {
                this.$set(uniqueRanges.labels, subValueProperty, subValues[subValueProperty]);
              });
              subPropertiesToDelete.forEach(subProp => {
                this.$delete(uniqueRanges.labels, subProp);
              });
            }
            if (!subValues || copyMainParamWithSubsProps) {
              this.$set(uniqueRanges.labels, property, value);
            }
          }
        }
      }
      this.isSetClassesDialogVisible = false;
    },
    openReclassify() {
      this.reclassifyDialogLoaded = true;
      this.reclassifyDialogVisible = true;
    },
    reclassifyAgree() {
      if (this.symbolizationType === 'unique') {
        this.classify(true);
      } else if (this.symbolizationType === 'ranges') {
        this.getRanges(true);
      }
      this.reclassifyDialogVisible = false;
    },
    openDeleteClass() {
      this.deleteClassLoaded = true;
      this.deleteClassVisible = true;
    },
    deleteClassAgree() {
      if (this.symbolizationType === 'unique') {
        this.deleteActiveClass();
      } else if (this.symbolizationType === 'ranges') {
        this.deleteActiveRange();
      }
      this.deleteClassVisible = false;
    },
    editLayerAction: call('layers/editLayer'),
    async saveDefaultStyle(layerId = this.layer.id, scope = this.layer.layer_scope, style = this.layer.style) {
      await this.editLayerAction({
        layer_id: layerId,
        layer_type: 'features_layers',
        body: { style_web: style },
        scope,
      });
    },
  },
  mounted() {
    this.$root.$off('changeSymbolizationKind');
    this.$root.$on('changeSymbolizationKind', this.changeSymbolizationKind);
    this.$root.$off('setClassesValues');
    this.$root.$on('setClassesValues', this.setClassesValues);
    this.unchangedStyle = JSON.parse(JSON.stringify(this.layer.style));
    if (this.layer.style.uniques) {
      this.symbolizationType = 'unique';
    } else if (this.layer.style.ranges) {
      this.symbolizationType = 'ranges';
    } else {
      this.symbolizationType = 'single';
    }
    if (this.layer.style['circle-color']) {
      this.symbolizationKind = 'point';
    } else if (this.layer.style['fill-color']) {
      this.symbolizationKind = 'polygon';
    } else if (this.layer.style['externalGraphic']) {
      this.symbolizationKind = 'icon';
    }
    this.symbolizationAttribute = this.layer.style.uniques ? this.layer.style.uniques.property : '';
    this.rangesAttribute = this.layer.style.ranges ? this.layer.style.ranges.property : '';
    this.numberOfClasses = this.layer.style.ranges ? this.layer.style.ranges.values.length : null;
    if (this.symbolizationType === 'unique') {
      this.setActiveClass(this.computedStyleUniquesValues[0]);
    } else if (this.symbolizationType === 'ranges') {
      const firstValue = this.computedStyleRangesValues[0];
      this.setActiveClass(firstValue);
      this.activeRadioStyleClass = `${firstValue['min-value']}-${firstValue['max-value']}`;
    }
    this.isValidClassification = true;
  },
};
</script>

<style lang="scss" scoped>
.title {
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.15);
  font-family: Roboto !important;
  font-style: normal !important;
  font-weight: normal !important;
  font-size: 18px !important;
  line-height: 16px !important;
  color: #202124 !important;
}
.style-elem-text {
  font-family: Roboto !important;
  font-style: normal !important;
  font-weight: 500 !important;
  font-size: 13px !important;
  line-height: 15px !important;
  color: #202124 !important;
  text-align: left;
  padding-left: 9px !important;
}
.button-style {
  margin-left: 5px;
  color: white;
  border-radius: 2px;
  font-family: Roboto !important;
  font-style: normal !important;
  font-weight: 500 !important;
  font-size: 12px !important;
  line-height: 16px !important;
  text-transform: none !important;
}
.style-select {
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 14px;
}
.border-top {
  border-top: 1px solid rgba(32, 33, 36, 0.18);
}
.border-bottom {
  border-bottom: 1px solid rgba(32, 33, 36, 0.18);
}
.border-right {
  border-right: 1px solid rgba(32, 33, 36, 0.18);
}
.single-class {
  border: 0.8px solid #898989;
  border-radius: 5px;
  text-align: left;
  float: left;
  min-height: 25px;
  min-width: 150px;
  padding-right: 5px;
  padding-left: 5px;
}
::v-deep .v-list-item__title {
  @include selectListElement();
}
.no-label {
  font-family: Roboto;
  font-style: italic;
  font-weight: 300;
  font-size: 12px;
  line-height: 20px;
  color: rgba(0, 0, 0, 0.87);
}
</style>
